import React, { useState } from 'react';
import { Alert, Portal, Snackbar } from '@mui/material';

/**
 * Page-script saves (docs/architecture/live-html-pages.md, section 8). A page's script calls `unity.save(element, text)`;
 * the frame only asks. This host validates the untrusted request, calls the server and answers the frame, and shows a
 * message itself, outside the frame, when the save is refused (so it appears even if the page's script ignores the error).
 */
export const MAX_SAVE_CHARS = 20000;
export type SaveCode = 'view_only' | 'public_link' | 'no_access' | 'not_writable' | 'too_long' | 'invalid_text' | 'page_held'
  | 'changed' | 'rate_limited' | 'failed';
/** `previousText` is the field's text as the frame last applied it (served, patched or saved): the server saves only while the field still holds it. */
export type SaveRequest = { uid: string; text: string; previousText: string };
export type SaveHandler = (request: SaveRequest) => Promise<{ version: number }>;

export class SaveError extends Error {
  code: SaveCode;
  constructor(code: SaveCode, holder?: string | null) {
    super(saveMessage(code, holder));
    this.code = code;
  }
}

export function saveMessage(code: SaveCode, holder?: string | null): string {
  switch (code) {
    case 'view_only': return "You can view this page but not save to it.";
    case 'public_link': return "Saving isn't available on a public link.";
    case 'no_access': return "You don't have access to save on this page.";
    case 'page_held': return `${holder || 'Someone'} is editing this page. Try again soon.`;
    case 'changed': return 'Someone else just changed this. The page now shows their version.';
    case 'rate_limited': return "You're saving too quickly. Try again in a few seconds.";
    case 'too_long': return "That's too long to save (20,000 characters at most).";
    case 'not_writable': return "This part of the page can't be saved.";
    case 'invalid_text': return "That text can't be saved.";
    default: return "Couldn't save. Check your connection and try again.";
  }
}

const UID = /^[a-z][a-z0-9]{5}$/;
const KNOWN: SaveCode[] = ['view_only', 'no_access', 'not_writable', 'too_long', 'invalid_text', 'page_held', 'changed', 'rate_limited'];
const codePoints = (text: string): number => {
  if (text.length <= MAX_SAVE_CHARS) return text.length;
  let count = 0;
  for (const _ of text) count++; // eslint-disable-line @typescript-eslint/no-unused-vars
  return count;
};

/** The server's refusal (or a transport failure) as a save error: the documented code and the person-facing message. */
export function saveErrorOf(error: unknown): SaveError {
  if (error instanceof SaveError) return error;
  const response = (error as { response?: { status?: number; data?: { code?: string; holder?: { name?: string } } } })?.response;
  if (!response) return new SaveError('failed');
  const code = String(response.data?.code || '');
  if ((KNOWN as string[]).includes(code)) return new SaveError(code as SaveCode, response.data?.holder?.name);
  if (response.status === 403) return new SaveError(code === 'view_only' ? 'view_only' : 'no_access');
  return new SaveError('failed');
}

/**
 * A frame's `save` message is an untrusted hint. Accept only a positive integer request id, a well-formed element identity
 * and text within the limit; the request to the server is built from these fields alone.
 */
export function readSaveMessage(data: unknown): { requestId: number; request: SaveRequest | null; error: SaveError | null } | null {
  const message = data as { requestId?: unknown; uid?: unknown; text?: unknown; previousText?: unknown } | null;
  if (!message || typeof message.requestId !== 'number' || !Number.isSafeInteger(message.requestId) || message.requestId < 1) return null;
  const { requestId } = message;
  if (typeof message.uid !== 'string' || !UID.test(message.uid) || typeof message.text !== 'string' || typeof message.previousText !== 'string') {
    return { requestId, request: null, error: new SaveError('not_writable') };
  }
  if (codePoints(message.text) > MAX_SAVE_CHARS) return { requestId, request: null, error: new SaveError('too_long') };
  return { requestId, request: { uid: message.uid, text: message.text, previousText: message.previousText }, error: null };
}

/** The message the host shows for a refused save: an MUI snackbar, never stacked twice for the same refusal while it is open. */
export function useSaveNotice() {
  const [notice, setNotice] = useState<{ text: string } | null>(null);
  const show = (error: SaveError) => setNotice(current => current?.text === error.message ? current : { text: error.message });
  // In a portal: the frame stage clips its children, and this message belongs to the whole window.
  const element = <Portal><Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={(_, reason) => { if (reason !== 'clickaway') setNotice(null); }}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ zIndex: theme => theme.zIndex.snackbar }}>
    <Alert severity="warning" variant="filled" onClose={() => setNotice(null)} role="alert" sx={{ maxWidth: 560 }}>{notice?.text}</Alert>
  </Snackbar></Portal>;
  return { show, element };
}
